import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Star,
  Filter,
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Globe,
  Smartphone,
  TrendingUp,
  ShoppingCart,
  Palette,
  Settings,
  Users,
  CheckSquare,
  Square,
  Zap,
  Loader2,
  Info,
  XCircle
} from 'lucide-react';
import { servicesAPI } from '../../services/api';
import DualEditor from './DualEditor';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const ServicesManagement = () => {
  const { notify } = useNotificationHook();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Bulk actions state
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const categories = ['Development', 'E-commerce', 'Marketing', 'Design', 'Consulting'];
  const iconOptions = ['Globe', 'Smartphone', 'ShoppingCart', 'TrendingUp', 'Star', 'Users'];

  // Fetch services from backend
  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await servicesAPI.getAll();
      
      if (response?.data?.success) {
        const servicesData = response.data.data.services || [];
        // Parse features field for each service
        const parsedServices = servicesData.map(service => ({
          ...service,
          features: typeof service.features === 'string' ? JSON.parse(service.features || '[]') : (service.features || [])
        }));
        setServices(parsedServices);
      } else {
        setError('Failed to load services');
        notify.error('Failed to load services', 'Loading Error');
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services');
      notify.error('Failed to load services. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // Filter services based on search and filters
  const filteredServices = services.filter(service => {
    const matchesSearch = service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || service.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || service.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const createService = async (serviceData) => {
    try {
      const response = await servicesAPI.create(serviceData);
      if (response.data.success) {
        await fetchServices(); // Refresh the list
        notify.success('Service created successfully!', 'Success');
        return true;
      } else {
        setError('Failed to create service');
        notify.error('Failed to create service', 'Creation Error');
        return false;
      }
    } catch (err) {
      setError('Error creating service');
      notify.error('Error creating service. Please try again.', 'Creation Error');
      console.error('Error creating service:', err);
      return false;
    }
  };

  const updateService = async (id, serviceData) => {
    try {
      const response = await servicesAPI.update(id, serviceData);
      if (response.data.success) {
        await fetchServices(); // Refresh the list
        notify.success('Service updated successfully!', 'Success');
        return true;
      } else {
        setError('Failed to update service');
        notify.error('Failed to update service', 'Update Error');
        return false;
      }
    } catch (err) {
      setError('Error updating service');
      notify.error('Error updating service. Please try again.', 'Update Error');
      console.error('Error updating service:', err);
      return false;
    }
  };

  const deleteService = async (id) => {
    try {
      const response = await servicesAPI.delete(id);
      if (response.data.success) {
        await fetchServices(); // Refresh the list
        notify.success('Service deleted successfully!', 'Success');
        return true;
      } else {
        setError('Failed to delete service');
        notify.error('Failed to delete service', 'Deletion Error');
        return false;
      }
    } catch (err) {
      setError('Error deleting service');
      notify.error('Error deleting service. Please try again.', 'Deletion Error');
      console.error('Error deleting service:', err);
      return false;
    }
  };

  const toggleServiceStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const response = await servicesAPI.toggleStatus(id, newStatus);
      if (response.data.success) {
        await fetchServices(); // Refresh the list
        notify.success(`Service status updated to ${newStatus}!`, 'Status Updated');
        return true;
      } else {
        setError('Failed to update service status');
        notify.error('Failed to update service status', 'Status Update Error');
        return false;
      }
    } catch (err) {
      setError('Error updating service status');
      notify.error('Error updating service status. Please try again.', 'Status Update Error');
      console.error('Error updating service status:', err);
      return false;
    }
  };

  const toggleServiceFeatured = async (id, currentFeatured) => {
    try {
      const response = await servicesAPI.toggleFeatured(id, !currentFeatured);
      if (response.data.success) {
        await fetchServices(); // Refresh the list
        notify.success(`Service ${!currentFeatured ? 'marked as' : 'removed from'} featured!`, 'Featured Status Updated');
        return true;
      } else {
        setError('Failed to update featured status');
        notify.error('Failed to update featured status', 'Featured Update Error');
        return false;
      }
    } catch (err) {
      setError('Error updating featured status');
      notify.error('Error updating featured status. Please try again.', 'Featured Update Error');
      console.error('Error updating featured status:', err);
      return false;
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fullDescription: '',
    icon: 'Globe',
    category: 'Development',
    price: '',
    duration: '',
    features: [''],
    status: 'active',
    featured: false,
    order: 1
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (window.confirm('Are you sure you want to remove this feature? This action cannot be undone.')) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clean up the form data
    const serviceData = {
      ...formData,
      features: formData.features.filter(f => f.trim() !== ''), // Remove empty features
      price: parseFloat(formData.price) || 0
    };
    
    let success = false;
    if (editingService) {
      // Update existing service
      success = await updateService(editingService.id, serviceData);
    } else {
      // Add new service
      success = await createService(serviceData);
    }
    
    if (success) {
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      fullDescription: '',
      icon: 'Globe',
      category: 'Development',
      price: '',
      duration: '',
      features: [''],
      status: 'active',
      featured: false,
      order: 1
    });
    setEditingService(null);
    setShowModal(false);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      fullDescription: service.fullDescription,
      icon: service.icon,
      category: service.category,
      price: service.price,
      duration: service.duration,
      features: service.features,
      status: service.status,
      featured: service.featured,
      order: service.order
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      await deleteService(id);
    }
  };

  const toggleStatus = async (id) => {
    const service = services.find(s => s.id === id);
    if (service) {
      await toggleServiceStatus(id, service.status);
    }
  };

  const toggleFeatured = async (id) => {
    const service = services.find(s => s.id === id);
    if (service) {
      await toggleServiceFeatured(id, service.featured);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      Globe: Globe,
      Smartphone: Smartphone,
      ShoppingCart: ShoppingCart,
      TrendingUp: TrendingUp,
      Star: Star,
      Users: Users
    };
    const IconComponent = icons[iconName] || Globe;
    return <IconComponent className="h-5 w-5" />;
  };

  // Bulk action functions
  const handleSelectService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedServices([]);
      setSelectAll(false);
    } else {
      setSelectedServices(services.map(service => service.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedServices.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await servicesAPI.bulkUpdateStatus(selectedServices, status);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedServices.length} services to ${status} status`, 'Status Updated');
        setSelectedServices([]);
        setSelectAll(false);
        await fetchServices();
      } else {
        notify.error('Failed to update services: ' + (response.data.message || 'Unknown error'), 'Update Error');
      }
    } catch (error) {
      console.error('Error updating services:', error);
      notify.error('Error updating services: ' + (error.response?.data?.message || error.message || 'Unknown error'), 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkFeaturedUpdate = async (featured) => {
    if (selectedServices.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await servicesAPI.bulkUpdateFeatured(selectedServices, featured);
      
      if (response.data.success) {
        notify.success(`Successfully ${featured ? 'set' : 'removed'} featured status for ${selectedServices.length} services`, 'Featured Status Updated');
        setSelectedServices([]);
        setSelectAll(false);
        await fetchServices();
      } else {
        notify.error('Failed to update services: ' + (response.data.message || 'Unknown error'), 'Update Error');
      }
    } catch (error) {
      console.error('Error updating services:', error);
      notify.error('Error updating services: ' + (error.response?.data?.message || error.message || 'Unknown error'), 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedServices.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedServices.length} services? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await servicesAPI.bulkDelete(selectedServices);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedServices.length} services`, 'Deleted');
        setSelectedServices([]);
        setSelectAll(false);
        await fetchServices();
      } else {
        notify.error('Failed to delete services: ' + (response.data.message || 'Unknown error'), 'Deletion Error');
      }
    } catch (error) {
      console.error('Error deleting services:', error);
      notify.error('Error deleting services: ' + (error.response?.data?.message || error.message || 'Unknown error'), 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Control bulk actions bar visibility
  useEffect(() => {
    setShowBulkActions(selectedServices.length > 0);
  }, [selectedServices]);

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Globe className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Services Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your service offerings and pricing
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Service
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border border-red-200 dark:border-red-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error}
                </p>
              </div>
            </div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 ml-4 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                  Success
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  {successMessage}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="flex-shrink-0 ml-4 text-green-400 hover:text-green-600 dark:hover:text-green-300 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mb-6 flex justify-center">
          <div className="flex items-center space-x-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <span className="text-blue-700 dark:text-blue-300 text-sm font-medium">
              Loading services...
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
        <button
          onClick={handleSelectAll}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
        >
          {selectAll ? <CheckSquare className="h-4 w-4 mr-2" /> : <Square className="h-4 w-4 mr-2" />}
          Select All
        </button>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        >
          <option value="all">All Categories</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl shadow-sm animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''} selected
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate('active')}
                  disabled={bulkActionLoading}
                  className="px-3 py-2 text-xs bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg flex items-center disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}
                  Set Active
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('inactive')}
                  disabled={bulkActionLoading}
                  className="px-3 py-2 text-xs bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg flex items-center disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <EyeOff className="h-3 w-3 mr-1" />}
                  Set Inactive
                </button>
                
                <button
                  onClick={() => handleBulkFeaturedUpdate(true)}
                  disabled={bulkActionLoading}
                  className="px-3 py-2 text-xs bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white rounded-lg flex items-center disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Star className="h-3 w-3 mr-1" />}
                  Set Featured
                </button>
                
                <button
                  onClick={() => handleBulkFeaturedUpdate(false)}
                  disabled={bulkActionLoading}
                  className="px-3 py-2 text-xs bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg flex items-center disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Star className="h-3 w-3 mr-1" />}
                  Remove Featured
                </button>
              </div>
            </div>
            
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="px-4 py-2 text-xs bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg flex items-center disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:p-6">
        {filteredServices.map((service) => (
          <div key={service.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <button
                  onClick={() => handleSelectService(service.id)}
                  className="flex items-center hover:text-blue-600 dark:hover:text-blue-400 mr-2"
                >
                  {selectedServices.includes(service.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                </button>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    {getIconComponent(service.icon)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{service.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{service.category}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {service.featured && (
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                  )}
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    service.status === 'active' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {service.status}
                  </span>
                </div>
              </div>

              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                {service.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Price:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    ₹{service.price.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Duration:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {service.duration}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Features:</p>
                <div className="flex flex-wrap gap-1">
                  {service.features && service.features.length > 0 ? (
                    <>
                      {service.features.slice(0, 3).map((feature, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded">
                          {feature}
                        </span>
                      ))}
                      {service.features.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded">
                          +{service.features.length - 3} more
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-500">No features listed</span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleStatus(service.id)}
                    className={`p-1 rounded ${
                      service.status === 'active' 
                        ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20'
                        : 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20'
                    }`}
                    title={service.status === 'active' ? 'Deactivate' : 'Activate'}
                  >
                    {service.status === 'active' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => toggleFeatured(service.id)}
                    className={`p-1 rounded ${
                      service.featured 
                        ? 'text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/20'
                        : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={service.featured ? 'Remove from featured' : 'Add to featured'}
                  >
                    <Star className={`h-4 w-4 ${service.featured ? 'fill-current' : ''}`} />
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(service)}
                    className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </h2>
                <button
                  onClick={resetForm}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Service Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Short Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Description *
                  </label>
                  <DualEditor
                    value={formData.fullDescription}
                    onChange={(content) => handleInputChange('fullDescription', content)}
                    placeholder="Enter full description with rich formatting..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Icon
                    </label>
                    <select
                      value={formData.icon}
                      onChange={(e) => handleInputChange('icon', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      {iconOptions.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price (₹) *
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Duration *
                    </label>
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={(e) => handleInputChange('duration', e.target.value)}
                      placeholder="e.g., 2-4 weeks"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Features
                  </label>
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Feature description"
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Feature
                  </button>
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => handleInputChange('featured', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Featured Service</span>
                  </label>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {editingService ? 'Update Service' : 'Add Service'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default ServicesManagement;
